import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToSection } from "../lib/scroll";
import { useHub } from "../lib/hubContent";
import { EditSectionButton } from "./HubEditor";
import Flower from "./Flower";
import Marquee from "./Marquee";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.14, type: "spring", stiffness: 90, damping: 13 },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.7 + i * 0.12, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const hub = useHub();
  const c = hub.section("hero");
  const marqueeItems = hub.section("marquee").items;
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "16%"]);
  const blockY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-10%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative bg-en-cream overflow-hidden flex flex-col min-h-screen pt-16">
      <EditSectionButton sectionKey="hero" label="Edit hero" />
      {/* color atmosphere */}
      <div className="absolute -top-24 -right-24 w-[34rem] h-[34rem] rounded-full bg-en-blue/25 blur-3xl" aria-hidden="true" />
      <div className="absolute top-1/3 -left-32 w-[28rem] h-[28rem] rounded-full bg-en-pink/25 blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-32 right-1/4 w-[26rem] h-[26rem] rounded-full bg-en-yellow/35 blur-3xl" aria-hidden="true" />
      <div className="absolute top-24 left-1/3 w-[16rem] h-[16rem] rounded-full bg-en-green/20 blur-3xl" aria-hidden="true" />

      {/* vertical editorial label */}
      <p className="hidden xl:block absolute left-4 top-1/2 -translate-y-1/2 rotate-180 [writing-mode:vertical-rl] font-mono text-[10px] tracking-[0.4em] uppercase opacity-40" aria-hidden="true">
        EST. EVERY DAY — A CREATIVE ECOSYSTEM
      </p>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 w-full flex-1 grid lg:grid-cols-12 gap-10 lg:gap-6 items-center py-14 md:py-20">
        {/* type block */}
        <motion.div style={{ y: blockY }} className="lg:col-span-7 relative z-10">
          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={0}
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 rounded-full border border-en-ink/15 bg-white/70 px-4 py-1.5 font-mono text-[10px] md:text-[11px] tracking-[0.3em] uppercase mb-7"
          >
            <span className="w-2 h-2 rounded-full bg-en-pink animate-pulse" />
            {c.eyebrow}
          </motion.p>

          <h1 data-testid="hero-title" className="font-display font-black uppercase tracking-tight leading-[0.9] text-en-ink">
            <span className="block overflow-hidden pb-1">
              <motion.span variants={line} initial="hidden" animate="show" custom={0} className="block text-[11.5vw] sm:text-6xl lg:text-7xl xl:text-[5.6rem]">
                Eclectically
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                variants={line}
                initial="hidden"
                animate="show"
                custom={1}
                className="block text-[22vw] sm:text-8xl lg:text-[7.5rem] xl:text-[9rem] italic text-transparent bg-clip-text bg-gradient-to-r from-en-blue via-en-purple to-en-pink"
              >
                Nelle
              </motion.span>
            </span>
          </h1>

          <motion.div variants={fade} initial="hidden" animate="show" custom={1} className="mt-7 flex flex-wrap items-center gap-4">
            <motion.span
              initial={{ scale: 0, rotate: -8 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 1.05, type: "spring", stiffness: 260, damping: 12 }}
              className="inline-block"
            >
              <span data-testid="hero-tagline" className="rounded-full bg-en-ink text-en-cream px-6 py-3 font-mono text-xs md:text-sm tracking-[0.25em] uppercase inline-block">
                {c.tagline}
              </span>
            </motion.span>
            <span className="font-hand text-2xl md:text-3xl text-en-pink -rotate-2">{c.hand_note}</span>
            <Flower className="w-8 h-8 animate-spin-slow" petals="#E26D3D" />
          </motion.div>

          <motion.p variants={fade} initial="hidden" animate="show" custom={2} data-testid="hero-subtext" className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-en-ink/75">
            {c.subtext}
          </motion.p>

          <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-9 flex flex-wrap items-center gap-5">
            <a
              href="#hub"
              data-testid="hero-explore-button"
              onClick={scrollToSection("#hub")}
              className="group inline-flex items-center gap-3 rounded-full bg-en-blue text-white px-7 py-3.5 font-mono text-xs tracking-[0.2em] uppercase hover:bg-en-ink transition-colors"
            >
              {c.cta_primary}
              <ArrowDown size={15} className="transition-transform group-hover:translate-y-0.5" />
            </a>
            <a
              href="#nelle"
              data-testid="hero-meet-nelle-link"
              onClick={scrollToSection("#nelle")}
              className="font-mono text-xs tracking-[0.2em] uppercase underline underline-offset-8 decoration-en-pink decoration-2 hover:text-en-pink transition-colors"
            >
              {c.cta_secondary}
            </a>
          </motion.div>
        </motion.div>

        {/* visual block */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: 3 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-sm lg:max-w-none"
          >
            <div className="absolute -inset-4 md:-inset-6 bg-en-yellow rounded-t-[999px] rotate-2" aria-hidden="true" />
            <div className="relative rounded-t-[999px] overflow-hidden border-4 border-en-ink aspect-[3/4] bg-en-cream">
              <motion.img
                src={c.image}
                alt={c.image_alt}
                data-testid="hero-image"
                style={{ y: imgY }}
                className="w-full h-[115%] object-cover object-[50%_30%] scale-110"
                loading="eager"
              />
            </div>

            <div className="absolute -left-6 md:-left-10 top-10 bg-white shadow-lg px-4 py-2 -rotate-6 animate-floaty" style={{ "--r": "-6deg" }}>
              <p className="font-hand text-xl text-en-ink">{c.sticker_1}</p>
            </div>
            <div className="absolute -right-4 md:-right-8 bottom-16 bg-en-green text-white shadow-lg px-4 py-2 rotate-3 animate-floaty" style={{ "--r": "3deg", animationDelay: "1.2s" }}>
              <p className="font-hand text-xl">{c.sticker_2}</p>
            </div>
            <div className="absolute -top-8 right-4 animate-spin-slow" aria-hidden="true">
              <Flower className="w-14 h-14" />
            </div>
            <div className="absolute -bottom-6 -left-6 animate-floaty" style={{ "--r": "-8deg", animationDelay: "0.7s" }} aria-hidden="true">
              <Flower className="w-11 h-11" petals="#4E9B94" center="#FBF3E4" />
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="relative border-y border-en-ink/15 bg-white/60 py-4"
      >
        <EditSectionButton sectionKey="marquee" label="Edit marquee" />
        <Marquee
          items={marqueeItems}
          itemClassName="font-display text-xl md:text-2xl tracking-wide text-en-ink"
        />
      </motion.div>
    </section>
  );
}
